import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 btn-press',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-button-default-hover active:bg-primary/80 shadow-sm',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-button-destructive-hover active:bg-destructive/80 shadow-sm',
        destructiveOutline:
          'border border-destructive/30 bg-background text-destructive hover:bg-destructive/10 active:bg-destructive/20',
        outline:
          'border border-button-outline-border bg-background hover:bg-button-outline-hover hover:text-accent-foreground active:bg-button-outline-hover/80',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-button-secondary-hover active:bg-secondary/70 shadow-sm',
        ghost:
          'hover:bg-button-ghost-hover hover:text-accent-foreground active:bg-button-ghost-hover/80',
        link: 'text-primary underline-offset-4 hover:underline active:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
